import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Lock, Mail, User, Eye, EyeOff, ArrowRight, Sparkles, Smartphone,
  Sun, Moon, Code, Book, Briefcase, Cpu, Music, Globe, FlaskConical, AlertCircle,
  Bomb, Movie, FoodForkKnife, MusicNote, TheaterMasks, Cocktail,
  FilmIcon, Star,
  Music2,
  TriangleAlert,
  DrumstickIcon,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';


const skillLevels = [
  { label: 'Beginner', value: 'beginner' },
  { label: 'Intermediate', value: 'intermediate' },
  { label: 'Advanced', value: 'advanced' }
];


const preferencesOptions = [
  { label: 'Sports', value: 'sports', icon: Bomb },
  { label: 'Entertainment', value: 'entertainment', icon: FilmIcon },
  { label: 'Food', value: 'food', icon: Briefcase },
  { label: 'Music', value: 'music', icon: Music2 },
  { label: 'Theatre', value: 'theatre', icon: TriangleAlert },
  { label: 'Drinks & Nightlife', value: 'nightlife', icon: DrumstickIcon },
];

const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [darkMode, setDarkMode] = useState(true);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    fullName: '',
    phone: '',    
    interests: []
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ message: '', type: '' }); // type: 'success' | 'error'

  const currentTheme = darkMode 
    ? {
        bg: 'bg-gradient-to-br from-slate-900 via-blue-900 to-emerald-900',
        text: 'text-blue-100',
        accent: 'from-blue-600 to-emerald-600',
        card: 'bg-slate-800/50 border-blue-500/20',
        button: 'bg-gradient-to-r from-blue-600 to-emerald-600 hover:from-blue-700 hover:to-emerald-700',
        input: 'bg-slate-800/30 border-blue-500/30 focus:border-blue-400',
        glass: 'bg-black/30 backdrop-blur-md',
        error: 'text-red-400'
      }
    : {
        bg: 'bg-gradient-to-br from-blue-50 via-emerald-50 to-white',
        text: 'text-slate-900',
        accent: 'from-blue-500 to-emerald-500',
        card: 'bg-white/70 border-blue-600/20',
        button: 'bg-gradient-to-r from-blue-500 to-emerald-500 hover:from-blue-600 hover:to-emerald-600',
        input: 'bg-white/80 border-blue-600/30 focus:border-blue-500',
        glass: 'bg-white/50 backdrop-blur-md',
        error: 'text-red-600'
      };

   const showAlert = (message, type = 'error') => {
    setAlert({ message, type });
    // Optional: automatically dismiss after 3-5 seconds
    setTimeout(() => {
      setAlert({ message: '', type: '' });
    }, 5000);
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Email validation (both login and signup)
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }   
    
    
    // Signup-specific validations
    if (!isLogin) {
      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (formData.password.length < 7) {
        newErrors.password = 'Password must be at least 7 characters';
      }

      if (!formData.fullName) {
        newErrors.fullName = 'Full name is required';
      }
      
      if (!formData.phone) {
        newErrors.phone = 'Phone number is required';
      } else if (!/^\d{10,15}$/.test(formData.phone)) {
        newErrors.phone = 'Please enter a valid phone number';
      }
      
           
      if (formData.interests.length === 0) {
        newErrors.interests = 'Please select at least one interest';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Add this useEffect hook at the beginning of your component
    useEffect(() => {
      const checkAuthStatus = async () => {
        try {
          const response = await fetch('/api/al/auth/verify', {
            method: 'GET',
            credentials: 'include' // Important for sending HTTP-only cookies
          });
  
          if (response.ok) {
            // User has valid token, redirect to home
            navigate('/AbiLanding');
          } else {
            // No valid token, stay on auth page
            //setIsCheckingAuth(false);
          }
        } catch (error) {
          //console.error('Auth verification error:', error);
          //setIsCheckingAuth(false);
        }
      };
  
      checkAuthStatus();
    }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleInterestChange = (value) => {
    setFormData(prev => {
      const newInterests = prev.interests.includes(value)
        ? prev.interests.filter(v => v !== value)
        : [...prev.interests, value];
      return { ...prev, interests: newInterests };
    });
    // Clear error when user selects an interest
    if (errors.interests) {
      setErrors(prev => ({ ...prev, interests: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);

    const url = isLogin ? '/api/al/auth/login' : '/api/al/auth/register';
    const payload = isLogin 
      ? { email: formData.email, password: formData.password }
      : formData;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload),
      });

      // Check if response is OK
      if (!response.ok) {
        // Try to read response as text, fallback to static message
        const errorText = await response.text();
        let errorMessage = 'An unexpected error occurred';

        // If the response is JSON, parse it to get specific message
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorMessage;
        } catch (e) {
          // Not JSON, keep static message
        }

        throw new Error(errorMessage);
      }

      const data = await response.json();

      // Proceed with success handling
      if (isLogin) {
        window.location.href = '/AbiLanding';
      } else {
        showAlert(data.message || 'Account created successfully!', 'success');
        setIsLogin(true);
        setFormData({ email: '', password: '', name: '', phone: '', interests: [] });
      }
    } catch (error) {
      // Show static message if response was an HTML page or other
      const message = error.message || 'Network error. Please try again.';
      showAlert(message, 'error');
      setErrors({ server: message });
    } finally {
    setLoading(false);
  }
};

  


  const toggleAuthMode = () => {
    setIsLogin(!isLogin);
    setErrors({});
  };

  return (
    <div className={`min-h-screen transition-colors duration-500 ${currentTheme.bg} ${currentTheme.text}`}>
      {/* Animated background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className={`absolute rounded-full ${darkMode ? 'bg-blue-400/10' : 'bg-emerald-600/10'}`}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              width: `${Math.random() * 10 + 5}px`,
              height: `${Math.random() * 10 + 5}px`,
            }}
            animate={{
              y: [0, -20, 0],
              x: [0, Math.random() * 40 - 20, 0],
              rotate: [0, Math.random() * 360],
            }}
            transition={{
              duration: Math.random() * 10 + 10,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        ))}
      </div>

      {alert.message && (
                <div
                  className={`fixed top-4 left-1/2 transform -translate-x-1/2 z-50 p-4 rounded-lg shadow-lg transition-colors duration-300 ${
                alert.type === 'success'
                  ? 'bg-green-500 text-white'
                  : 'bg-red-500 text-white'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                {alert.type === 'success' ? (
                  <Star className="w-5 h-5" />
                ) : (
                  <AlertCircle className="w-5 h-5" />
                )}
                <span className="max-w-xs">{alert.message}</span>
                  </div>
                </div>
              )}

      <div className="container mx-auto px-4 py-12 flex items-center justify-center min-h-screen">
        <div className="w-full max-w-md">
          {/* Logo & Theme toggle */}
          <div className="flex justify-between items-center mb-12">
            <div className="flex items-center space-x-3">
              <div className={`w-10 h-10 rounded-full bg-gradient-to-r ${currentTheme.accent} flex items-center justify-center`}>
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-light">ABILOOP</span>
            </div>
            <button
              onClick={() => setDarkMode(!darkMode)}
              className={`p-2 rounded-full ${darkMode ? 'bg-blue-900/30' : 'bg-blue-100'} transition-colors duration-300`}
            >
              {darkMode ? <Sun className="w-5 h-5 text-blue-400" /> : <Moon className="w-5 h-5 text-blue-600" />}
            </button>
          </div>

          {/* Auth Card */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className={`rounded-3xl ${currentTheme.glass} border ${darkMode ? 'border-blue-500/20' : 'border-blue-600/20'} shadow-2xl overflow-hidden`}
          >
            {/* Tab Switcher */}
            <div className="flex border-b border-blue-500/20">
              <button
                onClick={() => setIsLogin(true)}
                className={`flex-1 py-4 font-medium text-center relative ${isLogin ? currentTheme.text : darkMode ? 'text-blue-200/60' : 'text-slate-500'}`}
              >
                Sign In
                {isLogin && (
                  <motion.div 
                    layoutId="authTabIndicator"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-400 to-emerald-400"
                  />
                )}
              </button>
              <button
                onClick={() => setIsLogin(false)}
                className={`flex-1 py-4 font-medium text-center relative ${!isLogin ? currentTheme.text : darkMode ? 'text-blue-200/60' : 'text-slate-500'}`}
              >
                Sign Up
                {!isLogin && (
                  <motion.div 
                    layoutId="authTabIndicator"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-400 to-emerald-400"
                  />
                )}
              </button>
            </div>

            {/* Form Content */}
            <div className="p-8">
              <AnimatePresence mode="wait">
                <motion.div
                  key={isLogin ? 'login' : 'signup'}
                  initial={{ opacity: 0, x: isLogin ? -20 : 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: isLogin ? 20 : -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <h2 className="text-2xl font-light mb-8 text-center">
                    {isLogin ? 'Welcome Back' : 'Create Your Account'}
                  </h2>

                  <form onSubmit={handleSubmit} className="space-y-6" autoComplete="off">
                    {/* Signup Fields */}
                    {!isLogin && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Name */}
                        <div className="col-span-2">
                          <div className="relative">
                            <div className={`absolute left-4 top-1/2 transform -translate-y-1/2 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                              <User className="w-5 h-5" />
                            </div>
                            <input
                              type="text"
                              name="fullName"
                              placeholder="Full Name"
                              value={formData.fullName}
                              onChange={handleChange}
                              className={`w-full pl-12 pr-4 py-3 rounded-xl ${currentTheme.input} focus:ring-2 ${darkMode ? 'focus:ring-blue-500/30' : 'focus:ring-blue-600/30'} transition-all`}
                              autoComplete="off"
                            />
                          </div>
                          {errors.fullName && (
                            <div className="flex items-center mt-1 space-x-1 text-sm">
                              <AlertCircle className="w-4 h-4" />
                              <span className={currentTheme.error}>{errors.fullName}</span>
                            </div>
                          )}
                        </div>

                        {/* Email */}
                        <div className="col-span-2">
                          <div className="relative">
                            <div className={`absolute left-4 top-1/2 transform -translate-y-1/2 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                              <Mail className="w-5 h-5" />
                            </div>
                            <input
                              type="email"
                              name="email"
                              placeholder="Email Address"
                              value={formData.email}
                              onChange={handleChange}
                              className={`w-full pl-12 pr-4 py-3 rounded-xl ${currentTheme.input} focus:ring-2 ${darkMode ? 'focus:ring-blue-500/30' : 'focus:ring-blue-600/30'} transition-all`}
                              autoComplete="off"
                              required
                            />
                          </div>
                          {errors.email && (
                            <div className="flex items-center mt-1 space-x-1 text-sm">
                              <AlertCircle className="w-4 h-4" />
                              <span className={currentTheme.error}>{errors.email}</span>
                            </div>
                          )}
                        </div>

                        {/* Phone */}
                        <div className="col-span-2 md:col-span-1">
                          <div className="relative">
                            <div className={`absolute left-4 top-1/2 transform -translate-y-1/2 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                              <Smartphone className="w-5 h-5" />
                            </div>
                            <input
                              type="tel"
                              name="phone"
                              placeholder="Phone Number"
                              value={formData.phone}
                              onChange={handleChange}
                              className={`w-full pl-12 pr-4 py-3 rounded-xl ${currentTheme.input} focus:ring-2 ${darkMode ? 'focus:ring-blue-500/30' : 'focus:ring-blue-600/30'} transition-all`}
                              autoComplete="tel"
                            />
                          </div>
                          {errors.phone && (
                            <div className="flex items-center mt-1 space-x-1 text-sm">
                              <AlertCircle className="w-4 h-4" />
                              <span className={currentTheme.error}>{errors.phone}</span>
                            </div>
                          )}
                        </div>

                        {/* Password */}
                        <div>
                          <div className="relative">
                            <div className={`absolute left-4 top-1/2 transform -translate-y-1/2 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                              <Lock className="w-5 h-5" />
                            </div>
                            <input
                              type={showPassword ? 'text' : 'password'}
                              name="password"
                              placeholder="Password"
                              value={formData.password}
                              onChange={handleChange}
                              required
                              className={`w-full pl-12 pr-12 py-3 rounded-xl ${currentTheme.input} focus:ring-2 ${darkMode ? 'focus:ring-blue-500/30' : 'focus:ring-blue-600/30'} transition-all`}
                              autoComplete="current-password"
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              className={`absolute right-4 top-1/2 transform -translate-y-1/2 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}
                            >
                              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                            </button>
                          </div>
                          {errors.password && (
                            <div className="flex items-center mt-1 space-x-1 text-sm">
                              <AlertCircle className="w-4 h-4" />
                              <span className={currentTheme.error}>{errors.password}</span>
                            </div>
                          )}
                        </div>

                        {/* Skill Level 
                        <div className="col-span-2 md:col-span-1">
                          <div className="relative">
                            <select
                              name="skillLevel"
                              value={formData.skillLevel}
                              onChange={handleChange}
                              className={`w-full pl-4 pr-10 py-3 rounded-xl ${currentTheme.input} focus:ring-2 ${darkMode ? 'focus:ring-blue-500/30' : 'focus:ring-blue-600/30'} appearance-none transition-all`}
                            >
                              <option value="">Select Skill Level</option>
                              {skillLevels.map(level => (
                                <option key={level.value} value={level.value}>{level.label}</option>
                              ))}
                            </select>
                            <div className={`absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </div>
                          </div>
                          {errors.skillLevel && (
                            <div className="flex items-center mt-1 space-x-1 text-sm">
                              <AlertCircle className="w-4 h-4" />
                              <span className={currentTheme.error}>{errors.skillLevel}</span>
                            </div>
                          )}
                        </div>*/}

                        {/* Learning Interests */}
                        <div className="col-span-2">
                          <label className="block mb-2 text-sm font-medium">Learning Interests</label>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {preferencesOptions.map((interest) => {
                              const Icon = interest.icon;
                              const isSelected = formData.interests.includes(interest.value);
                              return (
                                <button
                                  type="button"
                                  key={interest.value}
                                  onClick={() => handleInterestChange(interest.value)}
                                  className={`flex items-center space-x-2 p-2 rounded-lg border transition-colors ${
                                    isSelected
                                      ? 'bg-blue-500/10 border-blue-500 text-blue-400'
                                      : `border-slate-700 hover:border-slate-600 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`
                                  }`}
                                >
                                  <Icon className="w-4 h-4" />
                                  <span className="text-xs">{interest.label}</span>
                                </button>
                              );
                            })}
                          </div>
                          {errors.interests && (
                            <div className="flex items-center mt-1 space-x-1 text-sm">
                              <AlertCircle className="w-4 h-4" />
                              <span className={currentTheme.error}>{errors.interests}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Login Fields */}
                    {isLogin && (
                      <>
                        {/* Email */}
                        <div>
                          <div className="relative">
                            <div className={`absolute left-4 top-1/2 transform -translate-y-1/2 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                              <Mail className="w-5 h-5" />
                            </div>
                            <input
                              type="email"
                              name="email"
                              placeholder="Email Address"
                              value={formData.email}
                              onChange={handleChange}
                              className={`w-full pl-12 pr-4 py-3 rounded-xl ${currentTheme.input} focus:ring-2 ${darkMode ? 'focus:ring-blue-500/30' : 'focus:ring-blue-600/30'} transition-all`}
                              autoComplete="email"
                            />
                          </div>
                          {errors.email && (
                            <div className="flex items-center mt-1 space-x-1 text-sm">
                              <AlertCircle className="w-4 h-4" />
                              <span className={currentTheme.error}>{errors.email}</span>
                            </div>
                          )}
                        </div>

                        {/* Password */}
                        <div>
                          <div className="relative">
                            <div className={`absolute left-4 top-1/2 transform -translate-y-1/2 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                              <Lock className="w-5 h-5" />
                            </div>
                            <input
                              type={showPassword ? 'text' : 'password'}
                              name="password"
                              placeholder="Password"
                              value={formData.password}
                              onChange={handleChange}
                              className={`w-full pl-12 pr-12 py-3 rounded-xl ${currentTheme.input} focus:ring-2 ${darkMode ? 'focus:ring-blue-500/30' : 'focus:ring-blue-600/30'} transition-all`}
                              autoComplete="current-password"
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              className={`absolute right-4 top-1/2 transform -translate-y-1/2 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}
                            >
                              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                            </button>
                          </div>
                          {errors.password && (
                            <div className="flex items-center mt-1 space-x-1 text-sm">
                              <AlertCircle className="w-4 h-4" />
                              <span className={currentTheme.error}>{errors.password}</span>
                            </div>
                          )}
                        </div>
                      </>
                    )}

                    {/* Remember Me & Forgot Password (Login only) */}
                    {isLogin && (
                      <div className="flex justify-between items-center">
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            className={`w-4 h-4 rounded ${darkMode ? 'bg-slate-700 border-blue-500' : 'bg-white border-blue-600'}`}
                          />
                          <span className={`text-sm ${darkMode ? 'text-blue-200/70' : 'text-slate-600'}`}>Remember me</span>
                        </label>
                        <a href="#" className={`text-sm ${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}>
                          Forgot password?
                        </a>
                      </div>
                    )}

                    {/* Submit Button */}
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      type="submit"
                      disabled={loading}
                      className={`w-full ${currentTheme.button} text-white py-4 rounded-xl font-medium flex items-center justify-center space-x-2 transition-all ${loading ? 'opacity-80' : ''}`}
                    >
                      {loading ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <>
                          <span>{isLogin ? 'Sign In' : 'Create Account'}</span>
                          <ArrowRight className="w-5 h-5" />
                        </>
                      )}
                    </motion.button>
                  </form>

                  {/* Toggle Sign Up / Sign In */}
                  <div className={`mt-6 text-center ${darkMode ? 'text-blue-200/70' : 'text-slate-600'}`}>
                    {isLogin ? "Don't have an account?" : 'Already have an account?'}{' '}
                    <button
                      onClick={toggleAuthMode}
                      className={`font-medium ${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}
                    >
                      {isLogin ? 'Sign up' : 'Sign in'}
                    </button>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>

          {/* Social Auth */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="mt-8"
          >
            <div className="relative">
              <div className={`absolute inset-0 flex items-center ${darkMode ? 'border-blue-500/20' : 'border-blue-600/20'}`}>
                <div className="w-full border-t"></div>
              </div>
              <div className="relative flex justify-center">
                <span className={`px-4 ${darkMode ? 'bg-slate-900/50 text-blue-200/70' : 'bg-white/70 text-slate-600' } text-sm`}>
                  Or continue with
                </span>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 mt-6">
              {['Google', 'Apple', 'Facebook'].map((provider, i) => (
                <motion.button
                  key={i}
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  className={`py-3 rounded-xl flex items-center justify-center ${darkMode ? 'bg-slate-800/30 border border-blue-500/20 hover:bg-slate-800/50' : 'bg-white border border-blue-600/20 hover:bg-gray-50'}`}
                >
                  <span className="text-sm font-medium">{provider}</span>
                </motion.button>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;